<template>
  <!--
    The only sticky element on a venue page, and the reason the movement reads
    cleanly now.

    It used to pin at top-14, beneath PjVenueBar — which was itself hidden and
    mid-transform on the way in. A sticky offset chasing a translate is why the
    strip looked like it was hunting for its own top edge, and
    PJ_VENUE_BAR_HEIGHT existed so three files could agree about that offset.
    Both the bar and the constant are gone.

    top-16 because PjAppHeader is sticky at top-0 and h-16, directly above this.
    That pair of classes is the whole contract between them — the header does not
    move, so there is nothing here chasing a transform, and nothing measures a
    height at runtime.

    z-30, one step under the header's z-bar, and that is load-bearing. Both sat
    at z-bar and this one comes later in the DOM, so it won the tie and painted
    over the account panel hanging out of the header above it. The panel belongs
    to the header, so the header has to outrank whatever it hangs over.
  -->
  <nav
    aria-label="Menu sections"
    class="sticky top-16 z-30 border-b bg-bg"
  >
    <!-- The search lies over the tabs rather than replacing them: the tabs keep
         their place, faded and inert, so the strip's height never changes. -->
    <Transition
      enter-active-class="transition-opacity duration-sheet ease-pj"
      enter-from-class="opacity-0"
      leave-active-class="transition-opacity duration-exit ease-pj"
      leave-to-class="opacity-0"
    >
      <div
        v-if="props.searching"
        class="pj-container absolute inset-0 z-10 flex items-center"
        :style="containerStyle"
      >
        <slot name="search" />
      </div>
    </Transition>
    <!-- One line at every width. Overflow scrolls sideways within the strip
         and never wraps, so the pinned header keeps a fixed height and adds no
         vertical scrolling of its own. The scrollbar itself is hidden. -->
    <!-- Same width as the dishes below, so a tab sits over its own column
         rather than near it. The padding lives on the scroll container so tabs
         run under it rather than stopping short of it. -->
    <ul
      ref="stripEl"
      class="pj-container scroll-none flex flex-nowrap gap-7 overflow-x-auto overflow-y-hidden pt-3 transition-opacity duration-sheet ease-pj"
      :class="{ 'opacity-0': props.searching }"
      :inert="props.searching"
      :style="[containerStyle, edgeFade]"
      @scroll="measureEdges"
    >
      <li>
        <button
          ref="searchEl"
          aria-label="Search the menu"
          class="-mb-px flex items-center border-b-tab border-transparent pb-3 text-muted pj-tap hover:text-ink"
          type="button"
          @click="emit('search')"
        >
          <PjIcon :name="PJ_ICON.SEARCH" />
        </button>
      </li>
      <li
        v-for="section in props.sections"
        :key="section.id"
      >
        <!-- Text with a brand rule under the active one, sitting on the
             hairline that closes the header. Not a pill: a pill needs a fill, a
             fill is a container, and the menu is uncontained.

             The rule reaches the window edge because the <nav> is a sibling of
             the page container rather than inside it — the strip is a band, and
             only its contents are centred. Escaping a container from within
             does not work: 100vw overflows by the reserved scrollbar gutter,
             and clipping that overflow clips the bleed with it. The active rule
             overlaps the hairline by 1px.

             Every tab carries the same weight, active included. Bolding the
             active one changes its width, and on a strip that re-picks its
             active tab continuously while you scroll, that reflow shows up as
             the whole row twitching. Colour and the rule carry the state. -->
        <button
          :ref="el => registerTab(section.id, el)"
          class="-mb-px whitespace-nowrap border-b-tab pb-3 text-body pj-tap"
          :class="
            section.id === props.activeId ? 'border-primary text-ink' : 'border-transparent text-muted hover:text-ink'
          "
          type="button"
          @click="emit('select', section.id)"
        >
          {{ section.name }}
        </button>
      </li>
    </ul>
  </nav>
</template>

<script lang="ts" setup>
import type { ComponentPublicInstance } from 'vue'

import { useResizeObserver } from '@vueuse/core'
import { computed, nextTick, ref, watch } from 'vue'

import type { PjContainerWidth } from '@/domains/pjazza/composables/use-container-width/useContainerWidth.ts'
import type { OrderingMenuSection } from '@/domains/pjazza/types/models/OrderingMenu.types.ts'

import { PJ_ICON } from '@/domains/pjazza/components/atoms/pj-icon/PjIcon.types.ts'
import PjIcon from '@/domains/pjazza/components/atoms/pj-icon/PjIcon.vue'
import { useContainerWidth } from '@/domains/pjazza/composables/use-container-width/useContainerWidth.ts'
import { prefersReducedMotion } from '@/utils/motion-util/motionUtil'

/**
 * Text with an underline when active. No pills — a pill needs a fill, and a
 * fill is a container.
 *
 * Tabs jump, section headings orient; the menu renders both, because a diner
 * who has scrolled into the middle of a long list needs to know where they are
 * without looking back up at the bar.
 *
 * Carries the categories and nothing else. The venue's name is stated once, in
 * the nav beside the wordmark, rather than in a second row of pinned chrome
 * directly above the hero that already says it.
 */
const props = defineProps<{
  sections: OrderingMenuSection[]
  activeId: string | null
  /** Must match the page container the dishes use, or the tabs sit off-column. */
  width?: PjContainerWidth
  /** True while the `search` slot lies over the tabs. */
  searching?: boolean
}>()

const emit = defineEmits<{ select: [sectionId: string]; search: [] }>()

defineSlots<{ search?: () => unknown }>()

const searchEl = ref<HTMLButtonElement | null>(null)

const containerStyle = useContainerWidth(() => props.width)

const stripEl = ref<HTMLElement | null>(null)

/**
 * How far the fade reaches in from a scrollable edge.
 *
 * Wide enough that a tab passing under it dissolves rather than clipping, narrow
 * enough that it never obscures a whole label.
 */
const FADE_PX = 32

const hasMoreBefore = ref(false)
const hasMoreAfter = ref(false)

/**
 * A fade only where there is actually something beyond the edge.
 *
 * Fading unconditionally is easier and wrong: it dims the first and last tab on a
 * short menu that does not scroll at all, which says "there is more" when there
 * is not. The whole point of the treatment is to tell the truth about that.
 */
const measureEdges = (): void => {
  const strip = stripEl.value

  if (!strip) return

  // A pixel of tolerance: scrollWidth and clientWidth are fractional under a
  // zoom or a fractional device ratio, so an exact comparison never arrives.
  hasMoreBefore.value = strip.scrollLeft > 1
  hasMoreAfter.value = strip.scrollLeft + strip.clientWidth < strip.scrollWidth - 1
}

/**
 * Masked rather than overlaid with a gradient in the page colour. The strip sits
 * on the ground, which flips with the theme — an overlay would have to know which
 * ground it is on, and would paint over the hairline underneath. A mask fades to
 * transparent and is right in both modes.
 */
const edgeFade = computed(() => {
  if (!hasMoreBefore.value && !hasMoreAfter.value) return {}

  const opening = hasMoreBefore.value ? `transparent 0, #000 ${FADE_PX}px` : '#000 0'
  const closing = hasMoreAfter.value ? `#000 calc(100% - ${FADE_PX}px), transparent 100%` : '#000 100%'
  const gradient = `linear-gradient(to right, ${opening}, ${closing})`

  return { maskImage: gradient, WebkitMaskImage: gradient }
})

// Content and width both change without a scroll happening — a different menu,
// a rotated phone, the container stepping at a breakpoint.
useResizeObserver(stripEl, measureEdges)

watch(
  () => props.sections,
  () => nextTick(measureEdges),
  { immediate: true }
)
const tabEls = new Map<string, HTMLElement>()

const registerTab = (id: string, el: Element | ComponentPublicInstance | null): void => {
  const node = (el as ComponentPublicInstance | null)?.$el ?? (el as HTMLElement | null)

  if (node) {
    tabEls.set(id, node as HTMLElement)

    return
  }

  // Unmounted — a menu can be replaced wholesale when the layout setting changes.
  tabEls.delete(id)
}

/**
 * A little of the neighbouring tab stays showing, so the strip still looks
 * scrollable once it has scrolled itself. Landing the active tab exactly against
 * the edge reads as the end of the list.
 */
const NEIGHBOUR_PEEK_PX = 28

/**
 * Follows the menu: the strip scrolls itself so the section you are reading is
 * the section you can see named.
 *
 * On a 37-section menu the active tab is off-screen within two flicks, and a
 * highlight nobody can see is the same as no highlight — the strip stops
 * answering "where am I" and, because its scrollbar is hidden, gives no hint
 * that the answer is sideways.
 *
 * Scrolls by a delta rather than calling scrollIntoView: that method walks every
 * scrollable ancestor, and this strip is sticky inside the page's own scroller,
 * so it would drag the menu vertically to satisfy a horizontal request. A no-op
 * when the tab is already in view, so scrolling within one long section stays
 * completely still.
 *
 * flush: 'post' because the tab for a newly-rendered section is only in the map
 * once the DOM has been patched.
 */
watch(
  () => props.activeId,
  activeId => {
    const strip = stripEl.value
    const tab = activeId ? tabEls.get(activeId) : null

    if (!strip || !tab) return

    const stripBox = strip.getBoundingClientRect()
    const tabBox = tab.getBoundingClientRect()

    // Nothing to reason about until the strip has been laid out. Without this the
    // all-zero boxes of an unmeasured element read as "the tab is off to the
    // left" and it scrolls a strip that has no width — which is what happens in
    // a test renderer, and would happen for real inside a hidden container.
    if (stripBox.width === 0) return

    const hiddenToTheLeft = stripBox.left - tabBox.left + NEIGHBOUR_PEEK_PX
    const hiddenToTheRight = tabBox.right - stripBox.right + NEIGHBOUR_PEEK_PX

    const delta = hiddenToTheLeft > 0 ? -hiddenToTheLeft : hiddenToTheRight > 0 ? hiddenToTheRight : 0

    if (delta === 0) return

    strip.scrollBy({ left: delta, behavior: prefersReducedMotion() ? 'auto' : 'smooth' })

    // scrollBy fires scroll events while it animates, so @scroll keeps the fade
    // in step — this covers the reduced-motion jump, which may not.
    measureEdges()
  },
  { flush: 'post' }
)

// The page closes the search by swapping this strip back in, then hands focus to where the diner opened it.
defineExpose({ focusSearch: () => searchEl.value?.focus() })
</script>
